import { useRef, useEffect, useCallback, useState } from "react";
import { motion, useInView, useAnimationControls } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { Reveal } from "./Reveal";
import { PaintSplat } from "./PaintSplat";
import { playBark } from "@/lib/sound";
import { HeartBurst } from "./HeartBurst";

const WORK = [
  { img: ASSETS.workKappa, title: "Lambda-Lambda Alumni Association", tag: "Website Design", caseStudy: "lambda-lambda", live: "kappasigmaelon.org", span: "md:col-span-6", ratio: "aspect-[16/10]" },
  { img: ASSETS.workMrac, title: "Meherrin River Arts Council", tag: "Website Design", caseStudy: "mrac", live: "mrac-arts.com", span: "md:col-span-6", ratio: "aspect-[16/10]" },
  { img: ASSETS.portPoster, title: "MRAC 2026 — Season Poster", tag: "Graphic Design", caseStudy: "mrac-poster", span: "md:col-span-4 md:col-start-5", ratio: "aspect-[2/3]" },
];

// Max pops in to "greet" visitors when the Portfolio scrolls into view, then
// settles into a happy little wag. Click him to bark, matching the hero.
const MaxCameo = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-90px" });
  const dog = useAnimationControls();
  const bubble = useAnimationControls();
  const isBarkingRef = useRef(false);
  const lastBarkRef = useRef(0);
  const [petCount, setPetCount] = useState(0);

  const wag = useCallback(() => {
    dog.start({
      rotate: [0, -5, 4, -4, 3, 0],
      y: [0, -4, 0, -3, 0],
      transition: { duration: 2.6, repeat: Infinity, ease: "easeInOut" },
    });
  }, [dog]);

  const woof = useCallback(() => {
    bubble.start({
      opacity: [0, 1, 1, 0],
      scale: [0.4, 1.1, 1, 0.7],
      y: [6, 0, 0, -4],
      transition: { duration: 1.4, times: [0, 0.15, 0.7, 1], ease: "easeOut" },
    });
  }, [bubble]);

  const bark = useCallback(async () => {
    const now = Date.now();
    if (isBarkingRef.current || now - lastBarkRef.current < 2000) return;
    lastBarkRef.current = now;
    isBarkingRef.current = true;
    playBark();
    woof();
    await dog.start({
      scale: [1, 1.16, 0.96, 1.06, 1],
      rotate: [0, -7, 5, -3, 0],
      transition: { duration: 0.6, ease: "easeInOut" },
    });
    isBarkingRef.current = false;
    wag();
  }, [dog, woof, wag]);

  // Pet = hover over: sparkle hearts + an excited wag, then back to the loop
  const pet = useCallback(() => {
    if (isBarkingRef.current) return;
    setPetCount((c) => c + 1);
    dog
      .start({
        rotate: [0, -9, 8, -6, 5, 0],
        transition: { duration: 0.9, ease: "easeInOut" },
      })
      .then(() => {
        if (!isBarkingRef.current) wag();
      });
  }, [dog, wag]);

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;
    (async () => {
      // ears-perk / peek-in pop
      await dog.start({
        opacity: 1,
        y: [40, -10, 0],
        scale: [0.65, 1.1, 1],
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
      });
      if (cancelled) return;
      woof(); // quick "Woof!" hello
      wag(); // happy wag loop
    })();
    return () => {
      cancelled = true;
    };
  }, [inView, dog, woof, wag]);

  return (
    <div
      ref={ref}
      className="relative shrink-0 w-[86px] md:w-[112px]"
      data-testid="portfolio-max-cameo"
    >
      {/* static glow (never transformed) */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full blur-2xl"
        style={{ background: "radial-gradient(circle, rgba(45,212,191,0.25), rgba(255,107,107,0.12) 50%, transparent 70%)" }}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.4, y: 6 }}
        animate={bubble}
        className="pointer-events-none absolute -top-3 -right-2 z-10 origin-bottom-left"
        data-testid="portfolio-max-bubble"
      >
        <div className="rounded-xl rounded-bl-none border border-white/15 bg-[#fafafa] px-2.5 py-1 shadow-[0_8px_20px_rgba(0,0,0,0.4)]">
          <span className="font-display font-bold uppercase tracking-tight text-[#09090b] text-xs leading-none">
            Woof!
          </span>
        </div>
      </motion.div>
      {/* Happy hearts when Max is petted (hovered) */}
      <HeartBurst trigger={petCount} count={4} iconSize={14} className="top-[10%] left-1/2" />
      <motion.img
        src={ASSETS.schnauzerDog}
        alt="Max the studio mascot. Hover to pet him, and he'll bark."
        loading="lazy"
        decoding="async"
        initial={{ opacity: 0, y: 40, scale: 0.65 }}
        animate={dog}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        onHoverStart={pet}
        onHoverEnd={() => bark()}
        onClick={bark}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            bark();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Pet Max the mascot — he'll wag, then bark"
        title="Pet me!"
        className="relative w-full select-none cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-teal/70 rounded-xl"
        data-testid="portfolio-max"
      />
    </div>
  );
};

const Card = ({ item, index, fill, className }) => {
  let Wrapper = "div";
  let wrapperProps = {};
  if (item.caseStudy) {
    Wrapper = Link;
    wrapperProps = { to: `/work/${item.caseStudy}` };
  } else if (item.href) {
    Wrapper = "a";
    wrapperProps = { href: item.href, target: "_blank", rel: "noopener noreferrer" };
  }
  const clickable = !!(item.caseStudy || item.href);
  return (
    <Reveal
      delay={index * 0.06}
      className={`${className ?? item.span ?? ""} group ${clickable ? "" : "cursor-pointer"} ${fill ? "h-full" : ""}`}
      data-testid={`work-item-${index}`}
    >
      <Wrapper {...wrapperProps} className={`block ${fill ? "h-full flex flex-col" : ""}`} data-testid={clickable ? `work-link-${index}` : undefined}>
        <div className={`relative overflow-hidden rounded-2xl border border-[#242424] ${fill ? "flex-1 min-h-[460px] bg-[#0a0a0c]" : item.ratio}`}>
          <motion.img
            src={item.img}
            alt={item.title}
            loading="lazy"
            decoding="async"
            className={`h-full w-full ${fill ? "object-contain p-3" : "object-cover"} brightness-[0.82] group-hover:brightness-100 transition-[filter,transform] duration-700 ${fill ? "" : "group-hover:scale-105"}`}
          />
          {item.live && (
            <span className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-[#050505]/75 backdrop-blur-md border border-[#333] px-3 py-1.5 font-mono-accent text-[10px] uppercase tracking-[0.18em] text-[#eaeaea]">
              <span className="h-1.5 w-1.5 rounded-full bg-teal animate-pulse" />
              Live · {item.live}
            </span>
          )}
          <div className="absolute top-4 right-4 grid place-items-center h-11 w-11 rounded-full bg-[#ff6b6b] text-[#0a0a0a] translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[opacity,transform] duration-500">
            <ArrowUpRight size={18} />
          </div>
        </div>

        <div className="flex items-start justify-between gap-4 pt-5">
          <div>
            <span className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#ff6b6b]">{item.tag}</span>
            <h3 className="font-display text-xl md:text-2xl tracking-tight mt-2 group-hover:text-[#ff6b6b] transition-colors duration-300">
              {item.title}
            </h3>
          </div>
          {item.caseStudy ? (
            <span className="shrink-0 mt-1 inline-flex items-center gap-1.5 font-mono-accent text-[10px] uppercase tracking-[0.18em] text-[#9a9a9a] group-hover:text-[#ff6b6b] transition-colors duration-300">
              Case Study <ArrowUpRight size={13} />
            </span>
          ) : item.href ? (
            <span className="shrink-0 mt-1 inline-flex items-center gap-1.5 font-mono-accent text-[10px] uppercase tracking-[0.18em] text-[#9a9a9a] group-hover:text-[#eaeaea] transition-colors duration-300">
              Visit <ArrowUpRight size={13} />
            </span>
          ) : null}
        </div>
      </Wrapper>
    </Reveal>
  );
};

export const Portfolio = () => (
  <section id="work" className="relative py-28 md:py-40 bg-[#0c0c0e] overflow-hidden" data-testid="work-section">
    <PaintSplat color="coral2" className="w-[480px] h-[480px] -top-16 -right-16" rotate={15} opacity={0.5} from={90} to={-90} />
    <PaintSplat color="orange3" className="w-[420px] h-[420px] -bottom-16 -left-20" rotate={185} opacity={0.44} from={-70} to={70} flip />
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 relative z-10">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 md:mb-24">
        <div className="max-w-2xl">
          <span className="font-mono-accent text-[11px] tracking-[0.3em] uppercase text-[#8b8b8b]">
            [ 02 ] &nbsp;Selected Work
          </span>
          <h2 className="font-display font-medium tracking-tighter text-4xl md:text-6xl mt-6 leading-[1.02]">
            A few things we're proud to have designed.
          </h2>
        </div>
        <div className="flex items-end gap-4 md:gap-5">
          <MaxCameo />
          <p className="text-[#9a9a9a] max-w-sm text-sm md:text-base leading-relaxed">
            Recent client work.
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:items-stretch">
        <div className="lg:col-span-7 flex flex-col gap-5">
          <Card item={WORK[0]} index={0} className="w-full" />
          <Card item={WORK[1]} index={1} className="w-full" />
        </div>
        <Card item={WORK[2]} index={2} fill className="lg:col-span-5" />
      </div>
    </div>
  </section>
);
